import Router from "next/router";
import { DateTime } from "luxon";
import { groupBy, includes } from "lodash";

export const asyncFilter = async (arr, predicate) =>
  Promise.all(arr.map(predicate)).then((results) =>
    arr.filter((_v, index) => results[index])
  );

export const asyncSome = async (arr, predicate) =>
  (await asyncFilter(arr, predicate)).length > 0;

export const redirect = ({ res, url }) => {
  if (res) {
    res.writeHead(307, { Location: url });
    return res.end();
  }

  return Router.push(url);
};

export const isExpiredCard = (month, year, validationDate) => {
  const expirationMonth = month;
  const expirationYear = year;

  let currentMonth = DateTime.now().month;
  let currentYear = DateTime.now().year;

  if (validationDate) {
    currentMonth = validationDate.month;
    currentYear = validationDate.year;
  }
  return expirationMonth < currentMonth && expirationYear <= currentYear;
};

const editableStatuses = ["pending", "approved", "checked-in"];
const customerEditableStatuses = ["pending", "approved", "rescheduling"];
const customerCancellableStatuses = ["approved", "rescheduling"];
const providerDeclineStatuses = ["pending"];
const providerCancellableStatuses = ["approved"];
export const canAppointmentEdit = (status) => {
  return editableStatuses.includes(status?.toLowerCase());
};

export const canCustomerEdit = (status, skipCustomerStatusUpdates = false) => {
  if (skipCustomerStatusUpdates) {
    return false;
  }
  return customerEditableStatuses.includes(status?.toLowerCase());
};

export const canCustomerCancel = ({
  appointmentStatus,
  requestedBy = "provider_request",
}) => {
  if (appointmentStatus === "pending" && requestedBy !== "provider_request") {
    return true;
  }
  return includes(customerCancellableStatuses, appointmentStatus);
};

export const canProviderDecline = (status, requestedBy) => {
  return (
    providerDeclineStatuses.includes(status?.toLowerCase()) &&
    requestedBy !== "provider_request"
  );
};

export const canProviderCancel = (status) => {
  return providerCancellableStatuses.includes(status?.toLowerCase());
};

export const getColorHexFromId = (id) => {
  // for use in profile placeholder icons
  const colors = [
    "#87C07F",
    "#F298C3",
    "#F6AB2B",
    "#81D2DB",
    "#AC8ED2",
    "#E4735A",
    "#7693DD",
  ];
  let val = Math.floor(Math.random() * 10);
  if (id) {
    val = parseInt(id.slice(-2), 16);
  }
  const color = Math.floor((colors.length * val) / 256);
  return colors[color];
};

export const goodBusinessHours = (data) => {
  const weekdays = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
  const result = [];
  const days = {};
  for (let i = 0; i < data.length; i += 1) {
    const atts = data[i].attributes;
    const start = atts.startTime.split("T")[1].split(":").slice(0, 2).join(":");
    const end = atts.endTime.split("T")[1].split(":").slice(0, 2).join(":");
    const hours = `${start}-${end}`;
    days[atts.dayOfWeek] = hours;
  }
  Object.keys(days)
    .sort()
    .forEach((day) => {
      result.push(`${weekdays[day]}: ${days[day]}`);
    });
  return result.join(", ");
};

export const mapBusinessItemData = (biz) => {
  const id = biz?.id;
  const friendlyName = biz?.attributes?.friendlyName;
  const urlLocality = biz?.attributes?.urlLocality;
  const urlRegion = biz?.attributes?.urlRegion;
  const list =
    biz?.include?.channelTimeSlots?.data ||
    biz?.include?.channelDateTimeSlots?.data;
  const groupedTimeslots = groupBy(list, (item) => {
    return item?.attributes?.dayOfWeek;
  });

  const serviceTypes = biz?.include?.serviceTypes?.data;
  const formattedAddress = biz?.attributes?.formattedAddress;
  const imageUrl = biz?.attributes.logo?.sizes?.medium?.url;
  const name = biz?.attributes?.businessName;
  const hours =
    biz?.include?.vendorBusinessOpenHours?.data?.[0]?.attributes || {};

  return {
    id,
    friendlyName,
    urlLocality,
    urlRegion,
    list,
    name,
    imageUrl,
    groupedTimeslots,
    serviceTypes,
    formattedAddress,
    ...hours,
  };
};

export const formatPhoneNumber = (value) => {
  if (!value) return value;
  let phoneNumber;
  if (value.slice(0, 2) === "+1") {
    phoneNumber = value.slice(2).replace(/[^\d]/g, "");
  } else {
    phoneNumber = value.replace(/[^\d]/g, "");
  }
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 10) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  const formatedNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
  if (phoneNumberLength > 9) {
    return `+1 (${phoneNumber.slice(-10, -7)}) ${phoneNumber.slice(
      -7,
      -4
    )}-${phoneNumber.slice(-4, phoneNumberLength + 1)}`;
  }
  return formatedNumber;
};

export const formatName = (value) => {
  if (!value) return value;

  const formattedName = value.replace(/[^A-Za-z ]+/g, "");

  return formattedName;
};
